import type { ReactNode } from 'react';
import { useEffect } from 'react';

interface AdminTranslationBoundaryProps {
    children: ReactNode;
}

const GOOGLE_NOTRANSLATE_META_SELECTOR = 'meta[data-admin-translation-guard="true"]';

export const AdminTranslationBoundary = ({ children }: AdminTranslationBoundaryProps) => {
    useEffect(() => {
        if (typeof document === 'undefined') {
            return undefined;
        }

        const html = document.documentElement;
        const body = document.body;
        const previousHtmlTranslate = html.getAttribute('translate');
        const previousBodyTranslate = body.getAttribute('translate');
        const hadHtmlNoTranslateClass = html.classList.contains('notranslate');
        const hadBodyNoTranslateClass = body.classList.contains('notranslate');
        const existingMeta = document.head.querySelector<HTMLMetaElement>(GOOGLE_NOTRANSLATE_META_SELECTOR);

        html.setAttribute('translate', 'no');
        body.setAttribute('translate', 'no');
        html.classList.add('notranslate');
        body.classList.add('notranslate');

        let insertedMeta = false;
        if (!existingMeta) {
            const meta = document.createElement('meta');
            meta.name = 'google';
            meta.content = 'notranslate';
            meta.dataset.adminTranslationGuard = 'true';
            document.head.appendChild(meta);
            insertedMeta = true;
        }

        return () => {
            if (previousHtmlTranslate === null) {
                html.removeAttribute('translate');
            } else {
                html.setAttribute('translate', previousHtmlTranslate);
            }

            if (previousBodyTranslate === null) {
                body.removeAttribute('translate');
            } else {
                body.setAttribute('translate', previousBodyTranslate);
            }

            if (!hadHtmlNoTranslateClass) {
                html.classList.remove('notranslate');
            }

            if (!hadBodyNoTranslateClass) {
                body.classList.remove('notranslate');
            }

            if (insertedMeta) {
                document.head.querySelector(GOOGLE_NOTRANSLATE_META_SELECTOR)?.remove();
            }
        };
    }, []);

    return (
        <div translate="no" className="notranslate min-h-screen" data-admin-translation-guard="true">
            {children}
        </div>
    );
};

export default AdminTranslationBoundary;
